// viewDocs (WEL-144) — the dependency-free logic behind the shared document
// viewer and the /docs list.
//
// Everything here is PURE: no React, no network, no `@/` imports — so it is
// unit-testable in isolation with node:test + tsx, the same way
// requestsSearch.ts is. The wire types in viewDocsClient.ts satisfy these
// shapes structurally; the shapes are declared loosely (all-optional) so a
// test fixture can be three fields wide.
//
// The one rule worth stating up front, because a bug here is a legal problem
// rather than a cosmetic one: `mayRecordOnBehalf` is the gate that keeps staff
// out of a signature. Spec decision 4 — "Staff may fill a form in on behalf of
// a recipient. Staff may NEVER sign." A 'pdf' surface is a signable, so it can
// never produce a fill affordance, whatever else the payload claims.

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** 'pdf' = a rendered/DocuSeal document; 'form' = questionnaire, no PDF. */
export type DocSurface = 'pdf' | 'form';

/** Chip tones, borrowed verbatim from the ops tier (oc-chip[data-tone]). */
export type DocTone = 'done' | 'blocked' | 'closed' | 'active' | 'active-soft';

/**
 * The row fields the search box reads.
 *
 * Spec decision 8: "one search box matching person · property · type". That is
 * exactly what this matches — the people, the property/unit, and the document
 * type (both its operator label and its raw kind, so typing "lease" finds
 * leases whatever we happen to call them this month). Deliberately NOT the
 * digest or the state: a row that matched on prose nobody typed at reads as a
 * bug, and state has its own filter.
 */
export interface SearchableDocRow {
  kind?: string | undefined;
  label?: string | undefined;
  people?: string | undefined;
  propertyLabel?: string | null | undefined;
  unitLabel?: string | null | undefined;
  /** Present but not this viewer's to read — see haystackOf. */
  withheld?: boolean | undefined;
}

export interface FilterableDocRow extends SearchableDocRow {
  id?: string | undefined;
  state?: string | undefined;
  /** ISO. The list is newest-first by this. */
  updatedAt?: string | undefined;
  sentAt?: string | null | undefined;
}

export interface DocFilters {
  q?: string | undefined;
  /** A document kind, or null/'' for "any type". */
  kind?: string | null | undefined;
  /** A document state, or null/'' for "any state". */
  state?: string | null | undefined;
}

// ---------------------------------------------------------------------------
// Search + filter
// ---------------------------------------------------------------------------

/**
 * The concatenated searchable text of a row — person · property · type.
 *
 * A WITHHELD row is searchable ONLY by its type. This is a real leak vector,
 * not a tidiness rule: if a person's name reached a withheld row, typing that
 * name and seeing an accommodation request appear would confirm the very
 * association the RRA roster exists to protect. The server withholds those
 * fields; this refuses to search them even if one slipped through.
 */
function haystackOf(row: SearchableDocRow): string {
  if (row.withheld) return [row.label, row.kind].filter(Boolean).join(' ');
  return [row.people, row.propertyLabel, row.unitLabel, row.label, row.kind]
    .filter(Boolean)
    .join(' ');
}

/**
 * True iff `row` matches the free-text `query`. An empty query matches
 * everything.
 *
 * The query is applied as a case-insensitive REGEX (so `^108` and
 * `lease|renewal` both work), degrading to a case-insensitive substring match
 * when it isn't valid regex — a stray `(` must never blank the list. Same
 * contract as the Requests search box, so staff only learn it once.
 */
export function matchesDocQuery(row: SearchableDocRow, query: string): boolean {
  const q = query.trim();
  if (!q) return true;
  const hay = haystackOf(row);
  try {
    return new RegExp(q, 'i').test(hay);
  } catch {
    return hay.toLowerCase().includes(q.toLowerCase());
  }
}

/** True iff the row survives all three filters (query, type, state). */
export function matchesDocFilters(row: FilterableDocRow, filters: DocFilters): boolean {
  if (filters.kind && row.kind !== filters.kind) return false;
  if (filters.state && row.state !== filters.state) return false;
  return matchesDocQuery(row, filters.q ?? '');
}

/** Filtered, newest-first. The list's whole ordering contract in one call. */
export function filterDocs<T extends FilterableDocRow>(rows: T[], filters: DocFilters): T[] {
  return sortDocsNewestFirst(rows.filter((r) => matchesDocFilters(r, filters)));
}

/** Timestamp used for ordering: when the document last moved. */
function movedAt(row: FilterableDocRow): string {
  return row.updatedAt ?? row.sentAt ?? '';
}

/**
 * Newest first, deterministically: last-moved descending, then sent
 * descending, then id descending. The id tie-break exists so two rows stamped
 * in the same second never swap places between renders.
 */
export function sortDocsNewestFirst<T extends FilterableDocRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const byMoved = movedAt(b).localeCompare(movedAt(a));
    if (byMoved !== 0) return byMoved;
    const bySent = (b.sentAt ?? '').localeCompare(a.sentAt ?? '');
    if (bySent !== 0) return bySent;
    return (b.id ?? '').localeCompare(a.id ?? '');
  });
}

/**
 * The distinct values of one field across the loaded rows, each with a label
 * and a count — the fallback when the API sends no facets. Ordered by count
 * descending, then label, so the common types sit at the top of the dropdown.
 */
export function deriveOptions<T extends FilterableDocRow>(
  rows: T[],
  field: 'kind' | 'state',
  labelOf: (row: T) => string,
): Array<{ value: string; label: string; count: number }> {
  const seen = new Map<string, { value: string; label: string; count: number }>();
  for (const row of rows) {
    const value = row[field];
    if (!value) continue;
    const hit = seen.get(value);
    if (hit) hit.count += 1;
    else seen.set(value, { value, label: labelOf(row), count: 1 });
  }
  return [...seen.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

// ---------------------------------------------------------------------------
// Version strip (spec decision 6)
// ---------------------------------------------------------------------------

/** "One row per document, version history inside it." */
export interface VersionLike {
  id: string;
  state?: string | undefined;
  /** ISO. */
  createdAt?: string | undefined;
  sentAt?: string | null | undefined;
  /** The version shown by default. Exactly one should carry it. */
  current?: boolean | undefined;
}

/**
 * Newest first, deterministically — created descending, then sent descending,
 * then id descending. The strip reads left-to-right as "now, then what came
 * before it", which is how an operator asks the question.
 */
export function orderVersions<T extends VersionLike>(versions: T[]): T[] {
  return [...versions].sort((a, b) => {
    const byCreated = (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    if (byCreated !== 0) return byCreated;
    const bySent = (b.sentAt ?? '').localeCompare(a.sentAt ?? '');
    if (bySent !== 0) return bySent;
    return b.id.localeCompare(a.id);
  });
}

/**
 * The version to open on: the one flagged `current`, else the newest.
 *
 * The fallback matters — a document whose every version is voided has no
 * current version, and the viewer must still open on something rather than
 * showing an empty frame.
 */
export function pickDefaultVersion<T extends VersionLike>(versions: T[]): T | null {
  if (versions.length === 0) return null;
  const ordered = orderVersions(versions);
  return ordered.find((v) => v.current) ?? ordered[0] ?? null;
}

/**
 * Resolve which version the viewer should show: the explicitly requested one
 * when it still exists, otherwise the default. A version id left over in the
 * URL after a revise must not strand the viewer on nothing.
 */
export function resolveSelectedVersion<T extends VersionLike>(
  versions: T[],
  requestedId?: string | null,
): T | null {
  if (requestedId) {
    const hit = versions.find((v) => v.id === requestedId);
    if (hit) return hit;
  }
  return pickDefaultVersion(versions);
}

/** States that mean "this version is no longer the live one". */
const CLOSED_VERSION_STATES: ReadonlySet<string> = new Set([
  'superseded',
  'voided',
  'cancelled',
  'declined',
  'expired',
]);

export function isClosedVersionState(state: string | undefined): boolean {
  return !!state && CLOSED_VERSION_STATES.has(state);
}

// ---------------------------------------------------------------------------
// State vocabulary
// ---------------------------------------------------------------------------

const STATE_TONES: Record<string, DocTone> = {
  draft: 'active-soft',
  sent: 'active',
  in_progress: 'active',
  partially_signed: 'active',
  opened: 'active',
  completed: 'done',
  executed: 'done',
  declined: 'blocked',
  failed: 'blocked',
  cancelled: 'closed',
  voided: 'closed',
  superseded: 'closed',
  expired: 'closed',
};

/**
 * Chip tone for a document/version state.
 *
 * An UNKNOWN state falls back to 'active' — plain foreground, legible in both
 * themes — never to a muted tone. A state we don't recognise yet is not the
 * same as a dead one, and a chip nobody can read in dark mode is how this went
 * wrong once before.
 */
export function docStateTone(state: string | undefined): DocTone {
  if (!state) return 'active';
  return STATE_TONES[state] ?? 'active';
}

/** 'in_progress' → 'In progress'. Only used when the API sends no label. */
export function humanizeDocState(state: string | undefined): string {
  if (!state) return 'Unknown';
  const words = state.replace(/[_-]+/g, ' ').trim();
  if (!words) return 'Unknown';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The API's wording when it sent one; a humanized code when it didn't. */
export function docStateLabel(row: {
  state?: string | undefined;
  stateLabel?: string | undefined;
}): string {
  return row.stateLabel?.trim() || humanizeDocState(row.state);
}

// ---------------------------------------------------------------------------
// Digest
// ---------------------------------------------------------------------------

/**
 * The one-line summary shown on a collapsed list row: whitespace collapsed to
 * single spaces, trimmed, and truncated on a WORD boundary with an ellipsis so
 * a long digest never breaks the row rhythm. An absent digest returns '' —
 * the row renders nothing rather than the word "undefined".
 */
export function formatDigest(digest: string | null | undefined, maxLength = 96): string {
  const clean = (digest ?? '').replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLength) return clean;
  const cut = clean.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  // Only break on a word boundary if there is a reasonable one — a single
  // 200-character token still has to be cut somewhere.
  const body = lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${body.replace(/[\s,;:.–—-]+$/, '')}…`;
}

/** "108 SE 6th St · #8", skipping whichever half we don't have. */
export function describeWhere(
  propertyLabel?: string | null,
  unitLabel?: string | null,
): string {
  return [propertyLabel, unitLabel].filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------------
// The fill-on-behalf gate (spec decision 4)
// ---------------------------------------------------------------------------

/**
 * May staff record answers on this document's behalf?
 *
 * TWO independent conditions, both required, and the surface check comes
 * first: a 'pdf' surface is a signable document, and staff may never sign. A
 * payload that claims `fillOnBehalf: true` on a pdf is a server bug, and this
 * function refuses it rather than rendering the affordance and finding out.
 * The API refuses it too (403 staff_may_not_sign) — two independent gates on
 * the one rule that cannot be allowed to fail.
 */
export function mayRecordOnBehalf(doc: {
  surface?: string | undefined;
  fillOnBehalf?: boolean | undefined;
}): boolean {
  if (doc.surface !== 'form') return false;
  return doc.fillOnBehalf === true;
}

/**
 * Kinds that have their OWN staff door, which a generic recorder must not
 * paper over. The accommodation forms carry legal semantics — a STATED answer
 * and a SIGNED attestation are different things, and the console that knows the
 * difference is where staff belong. The API answers 409 use_owning_surface;
 * we say so first, and point at the door.
 */
export const RECORD_OWNING_SURFACE: Readonly<Record<string, { href: string; label: string }>> = {
  rra_intake: { href: '/accommodations', label: 'the Accommodations console' },
  rra_verification: { href: '/accommodations', label: 'the Accommodations console' },
};

/**
 * Kinds this client can build a staff form for.
 *
 * `owner_intake` earns its place through a stable join rather than a
 * hand-copy: GET /api/docs/owner_intake/:id/questions returns the SAME
 * server-computed question list the owner's own form renders, and each
 * question's `id` IS the answers key it fills. Nothing about that form's
 * conditional logic lives here.
 */
const RECORD_FORMS: ReadonlySet<string> = new Set([
  'intake_designation',
  'intake_yard_care',
  'intake_animals',
  'rental_verification',
  'owner_intake',
]);

/**
 * Why staff cannot record here — a STATEMENT, never a hidden lock. Returns
 * null when recording is available.
 *
 * Only meaningful once mayRecordOnBehalf() has said yes; a signable renders no
 * affordance at all rather than an explanation nobody asked for.
 */
export function recordUnavailableReason(doc: {
  kind?: string | undefined;
  state?: string | undefined;
}): string | null {
  const owning = doc.kind ? RECORD_OWNING_SURFACE[doc.kind] : undefined;
  if (owning) {
    return `Accommodation answers are recorded in ${owning.label}, which keeps a stated answer and a signed attestation apart.`;
  }
  if (doc.state === 'completed') {
    return 'They have already answered. Their own answers stand.';
  }
  if (doc.state === 'cancelled') {
    return 'This request was cancelled, so there is nothing outstanding to record.';
  }
  if (doc.state === 'declined') {
    return 'They declined this request.';
  }
  if (doc.kind && !RECORD_FORMS.has(doc.kind)) {
    return 'Recording answers for this form is not built here yet.';
  }
  return null;
}

/** Which staff form to render. null when there is no form for this kind. */
export function recordFormForKind(kind: string | undefined): string | null {
  if (!kind) return null;
  return RECORD_FORMS.has(kind) ? kind : null;
}

// ---------------------------------------------------------------------------
// Addressing a document from a thread
// ---------------------------------------------------------------------------

/** The (kind, id) pair that addresses one document in the /api/docs world. */
export interface DocRef {
  kind: string;
  id: string;
}

/**
 * Which document a thread is showing.
 *
 * A thread's newest submission carries the authoritative pair — its
 * `documentType` IS the ViewDocKind and its `groupId` IS the document id, the
 * same addressing the current-state download endpoint already uses. A
 * document-backed thread with no submission yet (a generated-but-unsent lease)
 * falls back to the thread's own type + nativeId.
 *
 * Returns null when the thread has no single document to show — a pre-lease
 * intake thread spans three separate intake kinds, and picking one of them
 * would be showing the operator a document they didn't ask for.
 */
export function docRefForThread(input: {
  newestSubmission?: { documentType?: string | undefined; groupId?: string | undefined } | null | undefined;
  fallbackKind?: string | null | undefined;
  fallbackId?: string | null | undefined;
}): DocRef | null {
  const sub = input.newestSubmission;
  if (sub?.documentType && sub.groupId) {
    return { kind: sub.documentType, id: sub.groupId };
  }
  if (input.fallbackKind && input.fallbackId) {
    return { kind: input.fallbackKind, id: input.fallbackId };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Fill-on-behalf attribution (§C5)
// ---------------------------------------------------------------------------

/** How the answers reached WHP, in words that fit on the end of a sentence. */
const CHANNEL_WORDS: Readonly<Record<string, string>> = {
  email: 'by email',
  phone: 'by phone',
  in_person: 'in person',
  mail: 'by post',
  other: 'another way',
};

/** Just the attribution fields an activity event may carry. */
export interface AttributionMeta {
  recordedOnBehalf?: boolean | undefined;
  recordedBy?: string | undefined;
  recordedByDisplay?: string | undefined;
  onBehalfOf?: string | undefined;
  onBehalfOfDisplay?: string | undefined;
  channel?: string | undefined;
}

/**
 * The attribution line for a staff-recorded completion, or null for an
 * ordinary event.
 *
 * There is no separate "recorded" event by design — a staff-typed completion
 * IS a completion, and emitting two events would imply two things happened. So
 * the attribution rides on the completion event's meta, and this function is
 * what stops an answer WHP typed in from reading as one the recipient typed.
 *
 * Falls back to the raw ids when no display name came through: an id is ugly
 * but true, and dropping the line would hide who the record belongs to. An
 * unrecognised channel is passed through rather than swallowed.
 */
export function attributionLine(event: { meta?: AttributionMeta | undefined }): string | null {
  const meta = event.meta;
  if (!meta?.recordedOnBehalf) return null;
  const by = meta.recordedByDisplay?.trim() || meta.recordedBy?.trim() || 'a staff member';
  const forWhom = meta.onBehalfOfDisplay?.trim() || meta.onBehalfOf?.trim();
  const base = forWhom ? `Recorded by ${by} on behalf of ${forWhom}` : `Recorded by ${by}`;
  return meta.channel ? `${base} · ${CHANNEL_WORDS[meta.channel] ?? meta.channel}` : base;
}

// ---------------------------------------------------------------------------
// Owner-intake question predicates
//
// Pure, and here rather than beside the editor that renders them, because
// "which required questions are still unanswered" is a decision worth a test
// and a component import drags in import.meta.env.
// ---------------------------------------------------------------------------

/** Just the question fields these predicates read. */
export interface OwnerQuestionLike {
  id: string;
  prompt: string;
  required: boolean;
}

/**
 * True when the owner has not answered this question.
 *
 * `false` is an ANSWER, not an absence — a boolean question answered "no"
 * stores false, and treating that as unanswered would nag staff forever about a
 * question that was already settled. Only undefined/null and a blank string
 * count as unanswered.
 */
export function ownerQuestionUnanswered(
  answers: Record<string, unknown>,
  question: { id: string },
): boolean {
  const value = answers[question.id];
  if (value === undefined || value === null) return true;
  return typeof value === 'string' && value.trim() === '';
}

/** The prompts of every REQUIRED question still unanswered, in question order. */
export function ownerMissing(
  answers: Record<string, unknown>,
  questions: OwnerQuestionLike[],
): string[] {
  return questions
    .filter((q) => q.required && ownerQuestionUnanswered(answers, q))
    .map((q) => q.prompt);
}

// ---------------------------------------------------------------------------
// Restricted records
//
// Accommodation records are restricted to the RRA roster (rraAccess). The
// posture, which the household docket already takes, is to TELL the operator a
// restricted record exists rather than silently omit it — silence would
// misreport the file as empty, and an operator who hits this is doing their
// job, not breaking anything.
// ---------------------------------------------------------------------------

/** True when the row exists but its content is not this viewer's to read. */
export function isWithheldRow(row: { withheld?: boolean | undefined }): boolean {
  return row.withheld === true;
}

export const RESTRICTED_ROW_NOTE = 'Restricted — not yours to read';

/**
 * The standing one-line statement of what this list does NOT cover.
 *
 * Deliberately not an alert. The API cannot fetch or count restricted records,
 * so this note is unconditional for every viewer off the RRA roster — it
 * appears on every list load whether or not a single accommodation record
 * exists. At that frequency an alert would be a persistent warning about
 * nothing, and the cost of that is real: it teaches people to stop reading
 * alerts. This is a statement of scope, so it reads like one.
 *
 * The shared reason is de-duplicated: both accommodation kinds carry the same
 * sentence, and saying it twice would be worse than saying it once.
 */
export function describeWithheldKinds(
  notes: Array<{ kind: string; label?: string | undefined; reason: string }>,
): string | null {
  if (notes.length === 0) return null;
  const labels = [...new Set(notes.map((n) => n.label?.trim() || n.kind))];
  const reasons = [...new Set(notes.map((n) => n.reason.trim()).filter(Boolean))];
  const scope = `Not listed here: ${labels.join(', ')}`;
  return reasons.length > 0 ? `${scope} — ${reasons.join(' ')}` : scope;
}

export const RESTRICTED_DETAIL_NOTE =
  'Accommodation records are restricted to the accommodations roster, so this one cannot be opened here. Nothing is wrong — it exists, and someone on that roster can read it.';

/**
 * What an unopenable document actually is, so the viewer can respond in the
 * right voice instead of showing one generic error box.
 *
 *   'restricted'   — you may not read this. A statement, and NO retry: a retry
 *                    button on a permission boundary makes a working guard look
 *                    like a broken page.
 *   'not-viewable' — nothing to show yet (no panel written, nothing generated).
 *   'error'        — something actually went wrong; retrying may help.
 */
export function classifyDetailFailure(
  status: number | null,
  message?: string | null,
): { kind: 'restricted' | 'not-viewable'; text: string; retry: false } | { kind: 'error'; text: string; retry: true } {
  if (status === 403 || status === 401) {
    return { kind: 'restricted', text: message?.trim() || RESTRICTED_DETAIL_NOTE, retry: false };
  }
  if (status === 404) {
    return {
      kind: 'not-viewable',
      text:
        message?.trim() ||
        'This document is not viewable — its type has no details panel yet, so we will not guess at what it says.',
      retry: false,
    };
  }
  return {
    kind: 'error',
    text: message?.trim() || "Couldn't load the document.",
    retry: true,
  };
}

// ---------------------------------------------------------------------------
// The record body allowlist
//
// Staff may TRANSCRIBE an owner's answers. Staff may not COMPLETE an owner
// intake, because completing generates the EPMA and mails it for signature —
// outbound signable work, not transcription, and it belongs on the owner-intake
// page where that consequence is visible. W9's /record route has no completion
// path; this is the second lock, so the guarantee survives someone later adding
// a checkbox to the form without knowing why they shouldn't.
//
// Same shape of rule as mayRecordOnBehalf: state it once, structurally, on the
// boundary rather than trusting every future caller to remember.
// ---------------------------------------------------------------------------

/** The ONLY answer keys a record body may carry — one per fillable kind. */
export const RECORD_ANSWER_KEYS = [
  'designation',
  'yardCare',
  'animals',
  'verification',
  'answers',
] as const;

/** The ONLY envelope keys a record body may carry (§C5 attribution). */
export const RECORD_ENVELOPE_KEYS = [
  'onBehalfOf',
  'onBehalfOfDisplay',
  'channel',
  'note',
] as const;

/**
 * Assemble a record body from exactly the permitted keys and nothing else.
 *
 * Anything not on the two allowlists is DROPPED — notably `complete`, which
 * would turn a transcription into a send. Undefined envelope values are omitted
 * rather than sent as undefined, for the same reason the field merge deletes
 * them: absent means "not stated", and on owner intake it means "keep what is
 * on file".
 */
export function buildRecordBody(
  answers: Record<string, unknown>,
  envelope: Record<string, unknown> = {},
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const key of RECORD_ANSWER_KEYS) {
    if (answers[key] !== undefined) body[key] = answers[key];
  }
  for (const key of RECORD_ENVELOPE_KEYS) {
    const value = envelope[key];
    if (value !== undefined && value !== '') body[key] = value;
  }
  return body;
}
